// pages/admin/DatabaseExplorer.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { GlobalContext } from "../../context/GlobalContext";
import {
  Box,
  Stack,
  Chip,
  IconButton,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Pagination,
  Alert,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Delete,
  Edit,
  Save,
  Close,
  Refresh,
  Search,
  Code,
  TableChart,
  ContentCopy,
  CheckCircle,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

// ─── Helper ────────────────────────────────────────────────────────────────────
const sanitize = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

// ─── Edit Row Dialog ───────────────────────────────────────────────────────────
const EditRowDialog = ({ open, row, columns, onClose, onSave }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (row) {
      // Strip the internal __grid_id key we added
      const clean = { ...row };
      delete clean.__grid_id;
      setForm(clean);
    }
  }, [row]);

  if (!row) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={700}>Edit Row</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {columns.map((col) => (
            <TextField
              key={col.Field}
              label={col.Field}
              value={sanitize(form[col.Field])}
              onChange={(e) =>
                setForm({ ...form, [col.Field]: e.target.value })
              }
              size="small"
              fullWidth
              helperText={`Type: ${col.Type}${col.Key === "PRI" ? " · PRIMARY KEY" : ""}`}
              disabled={col.Key === "PRI"}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" startIcon={<Close />}>
          Cancel
        </Button>
        <Button
          onClick={() => onSave(form)}
          variant="contained"
          startIcon={<Save />}
          sx={{ boxShadow: 0, borderRadius: 2 }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── SQL Terminal ──────────────────────────────────────────────────────────────
const SqlTerminal = ({ hitAxios }) => {
  const [sql, setSql] = useState("SELECT * FROM users LIMIT 10");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  async function runSql() {
    setLoading(true);
    setError("");
    setResult(null);
    const res = await hitAxios({
      path: "/api/dbexplorer/run_query",
      post: true,
      admin: true,
      obj: { sql },
    });
    setLoading(false);
    if (res.data.success) {
      setResult(res.data);
    } else {
      setError(res.data.msg || "Query failed");
    }
  }

  function copyResult() {
    navigator.clipboard.writeText(JSON.stringify(result?.data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const resultColumns = result?.data?.length
    ? Object.keys(result.data[0]).map((k) => ({
        field: k,
        headerName: k,
        flex: 1,
        minWidth: 120,
        renderCell: (p) => (
          <Tooltip title={sanitize(p.value)}>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sanitize(p.value)}
            </span>
          </Tooltip>
        ),
      }))
    : [];

  return (
    <Box>
      <Stack spacing={2}>
        <Box sx={{ bgcolor: "#1e1e2e", borderRadius: 2, p: 2 }}>
          <Typography
            variant="caption"
            sx={{ color: "#888", mb: 1, display: "block" }}
          >
            SQL Terminal — SELECT / SHOW / DESCRIBE only
          </Typography>
          <TextField
            multiline
            minRows={4}
            maxRows={12}
            fullWidth
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === "Enter") runSql();
            }}
            sx={{
              "& .MuiInputBase-root": {
                bgcolor: "#2a2a3e",
                color: "#e0e0ff",
                fontFamily: "monospace",
                fontSize: 13,
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
          />
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            onClick={runSql}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Code />}
            sx={{ boxShadow: 0, borderRadius: 2 }}
          >
            Run Query (Ctrl+Enter)
          </Button>
          {result && (
            <Chip
              label={`${result.count} row${result.count !== 1 ? "s" : ""}`}
              color="success"
              size="small"
            />
          )}
          {result?.data?.length > 0 && (
            <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
              <IconButton size="small" onClick={copyResult}>
                {copied ? (
                  <CheckCircle fontSize="small" color="success" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {result?.data?.length > 0 && (
          <Box height={400} bgcolor="action.hover" borderRadius={2}>
            <DataGrid
              rows={result.data.map((r, i) => ({ _idx: i, ...r }))}
              getRowId={(r) => r._idx}
              columns={resultColumns}
              pageSize={25}
              rowsPerPageOptions={[25]}
              sx={{
                border: 0,
                fontSize: 12,
                fontFamily: "monospace",
                "& .MuiDataGrid-columnHeaders": { bgcolor: "action.selected" },
              }}
            />
          </Box>
        )}

        {result?.data?.length === 0 && (
          <Alert severity="info">Query returned 0 rows.</Alert>
        )}
      </Stack>
    </Box>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const DatabaseExplorer = () => {
  const { hitAxios } = React.useContext(GlobalContext);

  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [columnsLoading, setColumnsLoading] = useState(false); // ← NEW
  const [tabIndex, setTabIndex] = useState(0);
  const [searchCol, setSearchCol] = useState("");
  const [searchVal, setSearchVal] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [feedback, setFeedback] = useState({ msg: "", type: "success" });
  const feedbackTimer = useRef(null);

  const LIMIT = 50;

  function showFeedback(msg, type = "success") {
    setFeedback({ msg, type });
    clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(
      () => setFeedback({ msg: "", type: "success" }),
      3500,
    );
  }

  // ── Load tables on mount ───────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const res = await hitAxios({
        path: "/api/dbexplorer/get_tables",
        post: true, // ← was false
        admin: true,
        obj: {}, // ← empty body is fine
      });
      if (res.data.success) setTables(res.data.data);
    })();
  }, []);

  // ── Load columns when table changes ───────────────────────────────────────
  // FIX: We do NOT use a separate useEffect to trigger loadData anymore.
  //      Instead we load columns AND then immediately call loadData with the
  //      resolved firstCol — avoiding the stale-searchCol race condition.
  useEffect(() => {
    if (!selectedTable) return;

    // Reset everything synchronously before any async work
    setColumns([]);
    setRows([]);
    setTotal(0);
    setPage(1);
    setSearchCol("");
    setSearchVal("");

    (async () => {
      setColumnsLoading(true);
      const res = await hitAxios({
        path: `/api/dbexplorer/get_columns`, // ← no query string
        post: true, // ← was false
        admin: true,
        obj: { table: selectedTable },
        token_user: localStorage.getItem(
          process.env.REACT_APP_TOKEN + "_hamid",
        ),
      });
      setColumnsLoading(false);

      if (!res.data.success) {
        showFeedback(res.data.msg || "Failed to load columns", "error");
        return;
      }

      const fetchedColumns = res.data.data;
      const firstCol = fetchedColumns[0]?.Field || "";

      // Set columns state
      setColumns(fetchedColumns);
      setSearchCol(firstCol);

      // ✅ Call loadData directly here with resolved values — no stale closure
      await fetchTableData({
        table: selectedTable,
        cols: fetchedColumns,
        col: firstCol,
        val: "",
        pg: 1,
      });
    })();
  }, [selectedTable]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Core fetch function (takes explicit args, no stale closure risk) ───────
  const fetchTableData = useCallback(
    async ({ table, cols, col, val, pg }) => {
      if (!table || !cols?.length) return;
      setLoading(true);
      const res = await hitAxios({
        path: "/api/dbexplorer/get_table_data",
        post: true,
        admin: true,
        obj: {
          table,
          search: val,
          searchColumn: col,
          page: pg,
          limit: LIMIT,
        },
      });
      setLoading(false);
      if (res.data.success) {
        setRows(res.data.data);
        setTotal(res.data.total);
        setPage(pg);
      } else {
        showFeedback(res.data.msg || "Failed to load data", "error");
        setRows([]);
      }
    },
    [hitAxios],
  );

  // ── Public loadData (uses current state — safe after initial load) ─────────
  const loadData = useCallback(
    (pg = 1) => {
      fetchTableData({
        table: selectedTable,
        cols: columns,
        col: searchCol,
        val: searchVal,
        pg,
      });
    },
    [fetchTableData, selectedTable, columns, searchCol, searchVal],
  );

  // ── Primary key ────────────────────────────────────────────────────────────
  const pkColumn =
    columns.find((c) => c.Key === "PRI")?.Field || columns[0]?.Field;

  // ── Save edit ──────────────────────────────────────────────────────────────
  async function handleSave(form) {
    const updates = { ...form };
    delete updates[pkColumn];
    delete updates.__grid_id; // strip internal key
    const res = await hitAxios({
      path: "/api/dbexplorer/update_row",
      post: true,
      admin: true,
      obj: { table: selectedTable, pkColumn, pkValue: form[pkColumn], updates },
    });
    if (res.data.success) {
      showFeedback("Row updated successfully ✓");
      setEditOpen(false);
      loadData(page);
    } else {
      showFeedback(res.data.msg || "Update failed", "error");
    }
  }

  // ── Delete row ─────────────────────────────────────────────────────────────
  async function handleDelete(pkValue) {
    if (!window.confirm("Delete this row? This cannot be undone.")) return;
    const res = await hitAxios({
      path: "/api/dbexplorer/delete_row",
      post: true,
      admin: true,
      obj: { table: selectedTable, pkColumn, pkValue },
    });
    if (res.data.success) {
      showFeedback("Row deleted ✓");
      loadData(page);
    } else {
      showFeedback(res.data.msg || "Delete failed", "error");
    }
  }

  // ── Dynamic DataGrid columns ───────────────────────────────────────────────
  // FIX: Memoize so columns don't rebuild on every render causing DataGrid flicker
  const gridColumns = React.useMemo(() => {
    if (!columns.length) return [];
    return [
      {
        field: "__actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditRow(params.row);
                  setEditOpen(true);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(params.row[pkColumn])}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      ...columns.map((col) => ({
        field: col.Field,
        headerName: col.Field,
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <Tooltip title={sanitize(params.value)}>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: col.Type.includes("int") ? "monospace" : "inherit",
                color: col.Key === "PRI" ? "#7c4dff" : "inherit",
                fontWeight: col.Key === "PRI" ? 600 : 400,
              }}
            >
              {sanitize(params.value)}
            </span>
          </Tooltip>
        ),
      })),
    ];
  }, [columns, pkColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Safe grid rows — guarantee every row has a valid unique id ─────────────
  // FIX: Only build gridRows when columns are fully loaded (pkColumn is known)
  const gridRows = React.useMemo(() => {
    if (!pkColumn || !rows.length) return [];
    return rows.map((r, i) => ({
      __grid_id:
        r[pkColumn] !== undefined && r[pkColumn] !== null
          ? r[pkColumn]
          : `__fallback_${i}`,
      ...r,
    }));
  }, [rows, pkColumn]);

  const totalPages = Math.ceil(total / LIMIT);
  const isTableReady = !columnsLoading && columns.length > 0 && !!pkColumn;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <TableChart color="primary" />
        <Typography variant="h6" fontWeight={700}>
          Database Explorer
        </Typography>
        <Chip
          label={`${tables.length} tables`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Table Browser" icon={<TableChart />} iconPosition="start" />
        <Tab label="SQL Terminal" icon={<Code />} iconPosition="start" />
      </Tabs>

      {/* Feedback */}
      {feedback.msg && (
        <Alert severity={feedback.type} sx={{ mb: 2, borderRadius: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      {/* ══ TAB 0 — Table Browser ══ */}
      {tabIndex === 0 && (
        <Stack spacing={3}>
          {/* Controls bar */}
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
            >
              {/* Table picker */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Select Table</InputLabel>
                <Select
                  value={selectedTable}
                  label="Select Table"
                  onChange={(e) => setSelectedTable(e.target.value)}
                >
                  {tables.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Column picker — only show when ready */}
              {isTableReady && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Search Column</InputLabel>
                  <Select
                    value={searchCol}
                    label="Search Column"
                    onChange={(e) => setSearchCol(e.target.value)}
                  >
                    {columns.map((c) => (
                      <MenuItem key={c.Field} value={c.Field}>
                        {c.Field}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Search input — only show when ready */}
              {isTableReady && (
                <TextField
                  size="small"
                  placeholder="Search value... (Enter to search)"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadData(1)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton size="small" onClick={() => loadData(1)}>
                        <Search fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
              )}

              {/* Refresh */}
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    onClick={() => loadData(page)}
                    disabled={!isTableReady || loading}
                  >
                    {loading || columnsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Refresh />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>

          {/* Column schema chips */}
          {isTableReady && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {columns.map((col) => (
                <Chip
                  key={col.Field}
                  label={`${col.Field} (${col.Type})`}
                  size="small"
                  variant={col.Key === "PRI" ? "filled" : "outlined"}
                  color={col.Key === "PRI" ? "primary" : "default"}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          )}

          {/* Row count */}
          {isTableReady && total > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing{" "}
              <strong>
                {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)}
              </strong>{" "}
              of <strong>{total}</strong> rows in{" "}
              <strong>{selectedTable}</strong>
            </Typography>
          )}

          {/* Loading skeleton while columns load */}
          {columnsLoading && (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" mt={2}>
                Loading table schema...
              </Typography>
            </Stack>
          )}

          {/* ✅ DataGrid — only render when isTableReady AND not loading columns */}
          {isTableReady && !loading && gridRows.length > 0 && (
            <Box bgcolor="action.hover" borderRadius={3} height="60vh">
              <DataGrid
                rows={gridRows}
                getRowId={(r) => r.__grid_id}
                columns={gridColumns}
                pageSize={LIMIT}
                rowsPerPageOptions={[LIMIT]}
                hideFooter
                disableSelectionOnClick
                sx={{
                  border: 0,
                  borderRadius: 3,
                  fontSize: 12,
                  padding: 1,
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "action.selected",
                    fontWeight: 700,
                  },
                }}
              />
            </Box>
          )}

          {/* Loading rows spinner */}
          {isTableReady && loading && (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" mt={2}>
                Fetching rows...
              </Typography>
            </Stack>
          )}

          {/* Empty state */}
          {isTableReady && !loading && gridRows.length === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No rows found
              {searchVal ? ` matching "${searchVal}" in ${searchCol}` : ""}.
            </Alert>
          )}

          {/* Pagination */}
          {isTableReady && totalPages > 1 && (
            <Stack alignItems="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => loadData(p)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          )}
        </Stack>
      )}

      {/* ══ TAB 1 — SQL Terminal ══ */}
      {tabIndex === 1 && <SqlTerminal hitAxios={hitAxios} />}

      {/* Edit Dialog */}
      <EditRowDialog
        open={editOpen}
        row={editRow}
        columns={columns}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
};

export default DatabaseExplorer;
